import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { useContext, useEffect, useState } from "react";
import { UnmatchedShipmentType } from "types/ShippingTypes";
import { apiAssignUnmatched, apiGetUnmatched } from "util/network/Shipping";
import {
  ShipmentPage,
  carrierName,
  errorText,
  formatDateTime,
  formatKg,
  formatKr,
} from "./common";

/** Labels made in the Shipmondo UI whose reference matched no order. Assign
 *  one to an order by its order number; the lines are then picked on the
 *  order's packing page. */
const UnmatchedLabels: React.FC = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [rows, setRows] = useState<UnmatchedShipmentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderNos, setOrderNos] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    apiGetUnmatched(token)
      .then(setRows)
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const assign = (row: UnmatchedShipmentType) => {
    const orderNo = (orderNos[row.id] ?? "").trim();
    if (!orderNo) return;
    setBusyId(row.id);
    apiAssignUnmatched(token, row.id, orderNo)
      .then((shipment) => {
        setRows((current) => current.filter((r) => r.id !== row.id));
        addToast({
          type: "success",
          message: `Assigned to order ${shipment.orderNo}; pick its lines on the packing page`,
        });
      })
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setBusyId(null));
  };

  return (
    <ShipmentPage
      title="Unmatched labels"
      actions={
        <Tooltip title="Refresh">
          <IconButton onClick={load} sx={{ color: "#fff" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      {loading ? (
        <CircularProgress />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No unmatched labels.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Receiver</TableCell>
                <TableCell>Weight</TableCell>
                <TableCell>Cost (ex VAT)</TableCell>
                <TableCell>Booked</TableCell>
                <TableCell>Assign to order</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const receiver = row.receiver ?? {};
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      {carrierName(row.carrier)}{" "}
                      {row.trackingUrl ? (
                        <a
                          href={row.trackingUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {row.trackingNo}
                        </a>
                      ) : (
                        row.trackingNo
                      )}
                      <Typography variant="caption" display="block">
                        Shipmondo #{row.shipmondoId}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.shipmondoReference}</TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        {receiver.name}
                        {receiver.attention ? `, ${receiver.attention}` : ""}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {receiver.address1}, {receiver.zipcode} {receiver.city}{" "}
                        {receiver.country_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatKg(
                        row.parcels.reduce(
                          (sum, parcel) =>
                            sum + parcel.weightGrams * (parcel.quantity || 1),
                          0
                        )
                      )}
                    </TableCell>
                    <TableCell>{formatKr(row.priceExVat)}</TableCell>
                    <TableCell>{formatDateTime(row.bookedAt)}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="Order no"
                        value={orderNos[row.id] ?? ""}
                        onChange={(event) =>
                          setOrderNos({
                            ...orderNos,
                            [row.id]: event.target.value,
                          })
                        }
                        sx={{ width: 120, mr: 1 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={
                          busyId !== null || !(orderNos[row.id] ?? "").trim()
                        }
                        onClick={() => assign(row)}
                      >
                        Assign
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ShipmentPage>
  );
};

export default UnmatchedLabels;
